import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { useDentoData } from "../context/DentoContext";
import { LimelightNav } from "./ui/LimelightNav";

export const HospitalNavbar = ({ onAppointmentClick }) => {
    const { hospitalData } = useDentoData();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 40);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const navItems = [
        { id: 'home', label: 'Home', href: '#home' },
        { id: 'about', label: 'About', href: '#about' },
        { id: 'services', label: 'Services', href: '#services' },
        { id: 'doctor', label: 'Doctor', href: '#doctor' },
        { id: 'testimonials', label: 'Testimonials', href: '#testimonials' },
        { id: 'gallery', label: 'Gallery', href: '#gallery' },
        { id: 'contact', label: 'Contact', href: '#contact' }
    ];

    const scrollToSection = (href) => {
        setIsMobileMenuOpen(false);
        const el = document.querySelector(href);
        if (el) {
            const offset = 80; // Offset for sticky navbar
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = el.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const limelightItems = navItems.map(item => ({
        id: item.id,
        label: item.label,
        onClick: () => scrollToSection(item.href)
    }));

    return (
        <nav className={`fixed top-0 left-0 w-full z-[100] transition-all duration-300 ${isScrolled || isMobileMenuOpen
            ? 'bg-white shadow-md py-3'
            : 'bg-transparent py-5'
            }`}>
            <div className="container mx-auto px-6 flex justify-between items-center">
                {/* Logo - Positioned absolutely to overlap navbar without affecting height */}
                <div className="flex items-center">
                    <Link to="/" className="relative z-[110]">
                        <div className="absolute top-1/2 -translate-y-1/2 left-0 h-32 md:h-48 transition-transform hover:scale-105">
                            <img src="/logo/dentocare1.png" alt="Dento Care Logo" className="h-full w-auto object-contain drop-shadow-md" />
                        </div>
                        {/* Spacer to keep flex layout consistent */}
                        <div className="w-32 md:w-48 h-1" aria-hidden="true" />
                    </Link>
                </div>

                {/* Desktop: Limelight Navigation */}
                <div className="hidden lg:flex items-center">
                    <LimelightNav items={limelightItems} />
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-4 z-[110]">
                    <a
                        href={`tel:${hospitalData.emergency}`}
                        className="hidden md:flex items-center text-black font-bold px-4 py-2 rounded-full hover:bg-gray-100 transition-colors font-body text-sm whitespace-nowrap border border-gray-200"
                    >
                        <Phone className="w-4 h-4 mr-2" /> {hospitalData.emergency}
                    </a>
                    <button
                        onClick={onAppointmentClick}
                        className="bg-black text-white px-6 py-2.5 rounded-full font-bold text-sm hover:bg-black/80 transition-all shadow-lg active:scale-95 hidden sm:block font-heading tracking-wide"
                    >
                        Book Appointment
                    </button>

                    {/* Mobile Menu Toggle */}
                    <button
                        className="lg:hidden p-2 text-black transition-transform active:scale-90"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu Drawer */}
            <div className={`fixed inset-0 bg-white z-[105] lg:hidden transition-transform duration-300 ease-in-out transform ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
                }`}>
                <div className="flex flex-col h-full pt-24 pb-12 px-8 overflow-y-auto">
                    <div className="flex flex-col space-y-6">
                        {navItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => scrollToSection(item.href)}
                                className="text-3xl font-bold text-black uppercase tracking-widest text-left border-b border-gray-100 pb-4 flex justify-between items-center group font-heading"
                            >
                                {item.label}
                                <span className="text-gray-300 group-hover:translate-x-2 transition-transform">→</span>
                            </button>
                        ))}
                    </div>

                    <div className="mt-12 space-y-4">
                        <a
                            href={`tel:${hospitalData.emergency}`}
                            className="flex items-center justify-center bg-gray-50 text-black w-full py-5 rounded-2xl font-bold text-lg border border-gray-100 font-heading"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            <Phone className="w-5 h-5 mr-3" /> Call Clinic
                        </a>
                        <button
                            onClick={() => {
                                setIsMobileMenuOpen(false);
                                onAppointmentClick();
                            }}
                            className="bg-black text-white w-full py-5 rounded-2xl font-bold text-lg shadow-xl font-heading"
                        >
                            Book Appointment
                        </button>
                    </div>

                    <div className="mt-auto pt-10 text-center">
                        <p className="text-gray-400 text-sm font-body">© {new Date().getFullYear()} Dento Care</p>
                    </div>
                </div>
            </div>
        </nav>
    );
};

